import { useState, useEffect, useMemo, useCallback } from 'react'
import { error } from '../../../../../utils/logger'
import {
  runPipelineBatch,
  buildQualityReport,
  getSkipStages,
  refreshStoredConfig,
  loadGovernanceConfig,
  type PipelineBatchResult,
  type ValidationIssue,
  type GateVerdict,
} from '../../../../../governance'
import {
  ALL_QUESTIONS_CORE,
  MODULES,
  MODULE_CODES,
  type Question,
} from '../../../../../content'
import { VISUAL_AID_TYPES } from '../../../../../visuals'
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry'

// Inline gate threshold defaults to avoid extra import (matches issueContract.ts DEFAULT_GATE_THRESHOLDS)
const GATE_DEFAULTS = {
  productionMinScore: 70,
  assessmentMinScore: 82,
  reviewCoachSeverityThreshold: 45,
  assessmentMinQuestionsPerFamily: 3,
} as const

// ─── Module Code Resolution ───────────────────────────────────────────────────

/** Extract short module code from a question, handling TGL prefix correctly */
function resolveModuleCode(
  question: Question | undefined,
  questionId: string,
): string {
  // Prefer the question's module field (authoritative after TGL split)
  if (question?.module) {
    return MODULE_CODES[question.module] || question.module
  }
  // Fallback: parse from ID, handling TGL-GELD / TGL-TIJD prefix
  const parts = questionId.split('-')
  if (parts[0] === 'TGL' && parts.length >= 2) {
    if (parts[1] === 'GELD') return 'GELD'
    if (parts[1] === 'TIJD') return 'TIJD'
  }
  return parts[0] || 'UNKNOWN'
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UnifiedQuestionRow {
  questionId: string
  familyId: string
  moduleCode: string
  tier: number
  skill: string
  questionType: string
  // Pipeline
  verdict: GateVerdict
  pipelineScore: number
  pipelineIssues: ValidationIssue[]
  blockerCount: number
  warningCount: number
  // Quality
  qualityScore: number
  cognitiveLevel: number
  // Seed
  seedWarnings: string[]
  // Visual
  hasVisualProblems: boolean
}

export interface GovernanceStats {
  total: number
  pass: number
  fail: number
  review: number
  avgScore: number
  blockerCount: number
  warningCount: number
}

export interface ModuleHealth {
  code: string
  name: string
  total: number
  pass: number
  pct: number
  bgClass: string
}

export interface FamilyHealth {
  familyId: string
  total: number
  pass: number
  pct: number
}

export interface SystemStats {
  visualTypeCount: number
  questionTypeCount: number
  familyCount: number
  moduleCount: number
}

export function useGovernanceData() {
  const [isRunning, setIsRunning] = useState(true)
  const [lastRun, setLastRun] = useState<string | null>(null)
  const [runDuration, setRunDuration] = useState<string | null>(null)
  const [batchResult, setBatchResult] = useState<PipelineBatchResult | null>(
    null,
  )

  const totalQuestions = ALL_QUESTIONS_CORE.length

  const handleValidate = useCallback(() => {
    setIsRunning(true)
    const startTime = Date.now()
    const now = new Date()
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`

    // Use setTimeout to allow UI to render the loading state
    setTimeout(async () => {
      try {
        // Read settings and pass to pipeline
        await refreshStoredConfig()
        const skipStages = await getSkipStages()
        const govConfig = await loadGovernanceConfig()
        const skipPedagogy = govConfig.pipelineStages?.pedagogyEnabled === false

        // Convert stored quality gates to pipeline gate thresholds
        // This is the critical wiring: settings → pipeline behavior
        const thresholds = govConfig.qualityGates
          ? {
              productionMinScore:
                govConfig.qualityGates.minQualityScore ??
                GATE_DEFAULTS.productionMinScore,
              assessmentMinScore: Math.max(
                (govConfig.qualityGates.minQualityScore ?? 70) + 12,
                GATE_DEFAULTS.assessmentMinScore,
              ),
              reviewCoachSeverityThreshold:
                GATE_DEFAULTS.reviewCoachSeverityThreshold,
              assessmentMinQuestionsPerFamily:
                GATE_DEFAULTS.assessmentMinQuestionsPerFamily,
            }
          : undefined

        const result = runPipelineBatch(ALL_QUESTIONS_CORE, {
          ...(skipStages.length > 0 ? { skipStages } : {}),
          skipPedagogy,
          ...(thresholds ? { thresholds } : {}),
        })
        const duration = ((Date.now() - startTime) / 1000).toFixed(1)

        setBatchResult(result)
        setLastRun(`${timeStr} (${duration}s)`)
        setRunDuration(`${duration}s`)
      } catch (err) {
        error('Validation failed:', err)
      } finally {
        setIsRunning(false)
      }
    }, 50)
  }, [])

  // Auto-validate on mount
  useEffect(() => {
    handleValidate()
  }, [handleValidate])

  // Combine all data sources into unified rows
  const rows = useMemo<UnifiedQuestionRow[]>(() => {
    if (!batchResult) return []

    // 1. Get quality report
    const qualityReport = buildQualityReport(ALL_QUESTIONS_CORE)
    const qualityMap = new Map(
      qualityReport.scores.map((s) => [s.questionId, s]),
    )

    // 2. Build O(1) lookup for questions (fixes GOV-PERF O(n²) issue)
    const questionLookup = new Map(ALL_QUESTIONS_CORE.map((q) => [q.id, q]))

    // 3. Build unified rows
    return batchResult.results.map((result) => {
      const questionId = result.questionId

      // Find the original question via Map (O(1) instead of O(n))
      const question = questionLookup.get(questionId)

      // Use authoritative module code (handles TGL split)
      const moduleCode = resolveModuleCode(question, questionId)

      const quality = qualityMap.get(questionId)

      const blockers = result.gate.issues.filter((i) => i.kind === 'blocker')
      const warnings = result.gate.issues.filter((i) => i.kind === 'coach')

      // Check if any issue is visual related
      const hasVisualProblems = result.gate.issues.some(
        (i) => i.domain === 'visual',
      )

      return {
        questionId,
        familyId: question?.familyId || '',
        moduleCode,
        tier: question?.tier || 0,
        skill: question?.skill || '',
        questionType: question?.type || 'unknown',

        verdict: result.gate.verdict,
        pipelineScore: result.gate.score,
        pipelineIssues: result.gate.issues,
        blockerCount: blockers.length,
        warningCount: warnings.length,

        qualityScore: quality?.totalScore || 0,
        cognitiveLevel: quality?.dimensions.cognitiveLevel || 0,

        // Seed warnings now come from the pipeline (seed domain issues)
        seedWarnings: result.gate.issues
          .filter((i) => i.domain === 'seed')
          .map((i) => i.message),

        hasVisualProblems,
      }
    })
  }, [batchResult])

  // Calculate summary stats
  const stats = useMemo<GovernanceStats>(() => {
    if (!batchResult) {
      return {
        total: totalQuestions,
        pass: 0,
        fail: 0,
        review: 0,
        avgScore: 0,
        blockerCount: 0,
        warningCount: 0,
      }
    }

    let blockerCount = 0
    let warningCount = 0

    batchResult.results.forEach((r) => {
      blockerCount += r.gate.issues.filter((i) => i.kind === 'blocker').length
      warningCount += r.gate.issues.filter((i) => i.kind === 'coach').length
    })

    return {
      total: totalQuestions,
      pass: batchResult.verdicts.PASS,
      fail: batchResult.verdicts.FAIL,
      review: batchResult.verdicts.REVIEW_NEEDED,
      avgScore: batchResult.avgScore,
      blockerCount,
      warningCount,
    }
  }, [batchResult, totalQuestions])

  // Calculate module health — uses short codes (GET, REK, GELD, TIJD, etc.)
  const {
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    uniqueFamiliesCount,
  } = useMemo(() => {
    if (!batchResult)
      return {
        moduleHealth: [],
        familyHealthByModule: {},
        questionTypeDistribution: new Map<string, number>(),
        visualTypeDistribution: new Map<string, number>(),
        uniqueFamiliesCount: 0,
      }

    // Build O(1) lookup for questions (reuse pattern)
    const questionLookup = new Map(ALL_QUESTIONS_CORE.map((q) => [q.id, q]))

    const moduleStats = new Map<string, { total: number; pass: number }>()
    const familyStats = new Map<
      string,
      Map<string, { total: number; pass: number }>
    >()
    const qTypeDist = new Map<string, number>()
    const vTypeDist = new Map<string, number>()
    const uniqueFamilies = new Set<string>()

    // Initialize with all known modules using short codes
    MODULES.forEach((m) => {
      const code = MODULE_CODES[m.id] || m.id
      moduleStats.set(code, { total: 0, pass: 0 })
      familyStats.set(code, new Map())
    })

    // Count results using Map lookup (O(1) instead of O(n))
    batchResult.results.forEach((r) => {
      const question = questionLookup.get(r.questionId)
      const moduleCode = resolveModuleCode(question, r.questionId)
      const familyId = question?.familyId || 'UNKNOWN'

      const currentMod = moduleStats.get(moduleCode) || { total: 0, pass: 0 }
      moduleStats.set(moduleCode, {
        total: currentMod.total + 1,
        pass: currentMod.pass + (r.gate.verdict === 'PASS' ? 1 : 0),
      })

      if (!familyStats.has(moduleCode)) {
        familyStats.set(moduleCode, new Map())
      }
      const modFamilies = familyStats.get(moduleCode)!
      const currentFam = modFamilies.get(familyId) || { total: 0, pass: 0 }
      modFamilies.set(familyId, {
        total: currentFam.total + 1,
        pass: currentFam.pass + (r.gate.verdict === 'PASS' ? 1 : 0),
      })

      uniqueFamilies.add(familyId)

      if (question) {
        qTypeDist.set(question.type, (qTypeDist.get(question.type) || 0) + 1)

        // Count visual aids
        const allAids = [
          ...(question.supportAids || []),
          ...(question.stimulusAids || []),
        ]
        allAids.forEach((aid) => {
          if (aid.type) {
            vTypeDist.set(aid.type, (vTypeDist.get(aid.type) || 0) + 1)
          }
        })
      }
    })

    // Build code → module name lookup
    const codeToName = new Map<string, string>()
    MODULES.forEach((m) => {
      const code = MODULE_CODES[m.id] || m.id
      codeToName.set(code, m.title || code)
    })

    // Format for UI
    const formattedModuleHealth = Array.from(moduleStats.entries())
      .filter(([_, stats]) => stats.total > 0)
      .map(([code, stats]) => ({
        code,
        name: codeToName.get(code) || code,
        total: stats.total,
        pass: stats.pass,
        pct: stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0,
        bgClass: '', // Populated by UI using MODULE_CONFIG
      }))
      .sort((a, b) => b.total - a.total)

    const formattedFamilyHealth: Record<string, FamilyHealth[]> = {}
    familyStats.forEach((families, modCode) => {
      formattedFamilyHealth[modCode] = Array.from(families.entries())
        .map(([familyId, stats]) => ({
          familyId,
          total: stats.total,
          pass: stats.pass,
          pct:
            stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0,
        }))
        .sort((a, b) => a.familyId.localeCompare(b.familyId))
    })

    return {
      moduleHealth: formattedModuleHealth,
      familyHealthByModule: formattedFamilyHealth,
      questionTypeDistribution: qTypeDist,
      visualTypeDistribution: vTypeDist,
      uniqueFamiliesCount: uniqueFamilies.size,
    }
  }, [batchResult])

  // Get top blockers (worst 5 questions)
  const topBlockers = useMemo<UnifiedQuestionRow[]>(() => {
    return [...rows]
      .filter((r) => r.verdict === 'FAIL')
      .sort((a, b) => {
        if (b.blockerCount !== a.blockerCount)
          return b.blockerCount - a.blockerCount
        if (b.warningCount !== a.warningCount)
          return b.warningCount - a.warningCount
        return a.pipelineScore - b.pipelineScore
      })
      .slice(0, 5)
  }, [rows])

  const systemStats: SystemStats = useMemo(
    () => ({
      visualTypeCount: VISUAL_AID_TYPES.length,
      questionTypeCount: Object.keys(QUESTION_TYPE_REGISTRY).length,
      familyCount: uniqueFamiliesCount,
      moduleCount: MODULES.length,
    }),
    [uniqueFamiliesCount],
  )

  return {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    pipelineIssueCounts: batchResult?.issuesByDomain || null,
    isRunning,
    lastRun,
    runDuration,
    batchResult,
    revalidate: handleValidate,
  }
}
